/**
 * Preference insight (SEGMENT_INTRO) question UI.
 *
 * Belongs here: insight chrome (chips, ideal band, expert copy).
 * Does not belong here: flow rules.
 */
import { useEffect } from 'react'
import { EventNames } from '../../../../../../types/misc.types'
import { trackExpertReviewQuestionEvent } from '../../../analytics/expertReviewEvents'
import type { Question } from '../../../shared/types/questionnaire.types'
import RenderHTML from '../../../../../../components/ui-components/RenderHTML'
import { EXPERT_RECOMMENDATIONS_ICON } from '../../../shared/components/ExpertRecommendation'
import { buildPlanPercentage } from '../../../../plans-v4/display-values/lumpsumPlanPercentage'
import { usePreferencesContext } from '../../flow-engine/PreferencesProvider'
import { formatIdealAllocation } from '../../setup/config'

export const ROW_CHEVRON_ICON = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5 2L11 8L5 14" stroke="#8E9393" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

const CURRENT_ALLOCATION_ALERT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M7.75 14.75C11.616 14.75 14.75 11.616 14.75 7.75C14.75 3.88401 11.616 0.75 7.75 0.75C3.88401 0.75 0.75 3.88401 0.75 7.75C0.75 11.616 3.88401 14.75 7.75 14.75Z" stroke="#CC5C2D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.75 4.51953V8.01953" stroke="#CC5C2D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.75019 10.9808C7.6015 10.9808 7.48096 10.8603 7.48096 10.7116C7.48096 10.5629 7.6015 10.4424 7.75019 10.4424" stroke="#CC5C2D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.75049 10.9808C7.89918 10.9808 8.01972 10.8603 8.01972 10.7116C8.01972 10.5629 7.89918 10.4424 7.75049 10.4424" stroke="#CC5C2D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`

/** FE-owned insight chrome labels (CTA / card headings). */
export function getPreferenceInsightCopy(_preferenceTypeId?: string) {
  return {
    nextButtonLabel: 'View funds & share preference',
    expertRecommendationsHeading: 'Expert Recommendations',
    currentAllocationHeading: 'Current allocation',
    recommendedHeading: 'Recommended'
  }
}

interface PreferenceInsightQuestionProps {
  question: Question
  analyticsScreen: string
  contextId?: string
}

export function PreferenceInsightQuestion({
  question,
  analyticsScreen,
  contextId
}: PreferenceInsightQuestionProps) {
  const { currentPreferenceType } = usePreferencesContext()
  const copy = getPreferenceInsightCopy(currentPreferenceType?.id)
  const questionId = question.id

  useEffect(() => {
    trackExpertReviewQuestionEvent({
      eventName: EventNames.ScreenLoaded,
      ScreenName: analyticsScreen,
      questionId,
      scrollSection: contextId
    })
  }, [analyticsScreen, questionId, contextId])

  const groupChips = (currentPreferenceType?.groups ?? [])
    .map((group) => group.groupName?.trim() ?? '')
    .filter((groupName) => groupName !== '')
  const currentAllocation = buildPlanPercentage({
    rawPercentage: currentPreferenceType?.portfolioPercent ?? 0
  })
  const recommendedLabel = formatIdealAllocation(
    question.expertRecommendation?.idealAllocationPercentage
  )
  const recommendation = question.expertRecommendation

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 text-[#232323] text-[22px] leading-[130%]">
        <p className="font-[400]">{question.displayText}</p>
      </div>

      {groupChips.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {groupChips.map((groupName) => (
            <span
              key={groupName}
              className="rounded-[8px] bg-[#FFEDEB] px-2 py-2 text-[14px] font-[500] leading-[130%] text-[#232323]"
            >
              {groupName}
            </span>
          ))}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-[16px] border border-white bg-[#FFEDEB]">
        <div className="flex items-center gap-4 py-3 pl-[15px]">
          <div className="h-[18px] w-[18px] shrink-0 overflow-hidden">
            <RenderHTML
              innerHTMLText={EXPERT_RECOMMENDATIONS_ICON}
              className="h-full w-full [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
            />
          </div>
          <p className="text-[14px] font-[500] leading-[130%] text-[#232323]">
            {copy.expertRecommendationsHeading}
          </p>
        </div>
        <div className="flex flex-col gap-4 rounded-[15px] bg-white px-5 py-6">
          <div className="flex items-center justify-center gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
                {copy.currentAllocationHeading}
              </p>
              <div className="flex items-center gap-2">
                <p className="text-[22px] font-[500] leading-[130%] text-[#232323]">
                  {currentAllocation.label}
                </p>
                {currentAllocation.value > 10 ? (
                  <span className="h-4 w-4 shrink-0" aria-hidden>
                    <RenderHTML
                      innerHTMLText={CURRENT_ALLOCATION_ALERT_ICON}
                      className="h-full w-full [&>svg]:block [&>svg]:h-full [&>svg]:w-full"
                    />
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <p className="text-right text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
                {copy.recommendedHeading}
              </p>
              <p className="text-right text-[22px] font-[500] leading-[130%] text-[#232323]">
                {recommendedLabel}
              </p>
            </div>
          </div>
          <div className="h-px w-full bg-[#F0F4F7]" />
          {recommendation ? (
            <div className="flex flex-col gap-2 pr-4 text-[13px] leading-[150%]">
              <p className="font-[500] text-[#CC5C2D]">{recommendation.displayText}</p>
              <p className="font-[400] text-[#6A6E6E]">{recommendation.subText}</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
